import { StateCreator } from 'zustand'
import { Service, ServiceCategory } from '@types'

export interface ServicesSlice {
  services: {
    items: Service[]
    isLoading: boolean
    error: string | null
    selectedCategory: ServiceCategory | 'all'
    fetchServices: () => Promise<void>
    setSelectedCategory: (category: ServiceCategory | 'all') => void
    getServicesByCategory: (category: ServiceCategory | 'all') => Service[]
  }
}

const mockServices: Service[] = [
  {
    id: '1',
    title: 'Digital Twins',
    description: 'Creamos gemelos digitales precisos que replican sistemas del mundo real, permitiendo simulaciones avanzadas y análisis predictivos.',
    icon: '🌐',
    features: [
      'Simulación en tiempo real',
      'Análisis predictivo',
      'Integración IoT',
      'Visualización 3D',
      'Monitoreo remoto',
    ],
    technologies: ['Unity 3D', 'Unreal Engine', 'IoT', 'AI/ML'],
    priceRange: { min: 15000, max: 100000, currency: 'USD' },
    duration: '8-16 semanas',
    category: 'digital-twins',
  },
  {
    id: '2',
    title: 'Metaversos',
    description: 'Desarrollamos mundos virtuales inmersivos y plataformas sociales en 3D que conectan usuarios globalmente.',
    icon: '🚀',
    features: [
      'Mundos 3D personalizados',
      'Avatares personalizables',
      'Interacción social',
      'Eventos virtuales',
      'Integración blockchain',
    ],
    technologies: ['WebXR', 'Three.js', 'WebGL', 'Blockchain'],
    priceRange: { min: 25000, max: 150000, currency: 'USD' },
    duration: '12-24 semanas',
    category: 'metaversos',
  },
  {
    id: '3',
    title: 'Meta Humans',
    description: 'Diseñamos avatares hiperrealistas y asistentes virtuales con inteligencia artificial avanzada.',
    icon: '👤',
    features: [
      'Avatares hiperrealistas',
      'IA conversacional',
      'Expresiones faciales',
      'Sincronización de voz',
      'Personalización completa',
    ],
    technologies: ['Unreal Engine', 'AI/ML', 'Facial Recognition', 'NLP'],
    priceRange: { min: 10000, max: 75000, currency: 'USD' },
    duration: '6-12 semanas',
    category: 'meta-humans',
  },
  {
    id: '4',
    title: 'Consultoría VR',
    description: 'Asesoramos empresas en la implementación estratégica de tecnologías VR.',
    icon: '🎯',
    features: [
      'Análisis de necesidades',
      'Estrategia de implementación',
      'Selección de tecnologías',
      'ROI y métricas',
      'Capacitación de equipos',
    ],
    technologies: ['VR/AR', 'Strategy', 'Analytics', 'Training'],
    priceRange: { min: 5000, max: 50000, currency: 'USD' },
    duration: '2-8 semanas',
    category: 'consultoria',
  },
  {
    id: '5',
    title: 'Desarrollo Personalizado',
    description: 'Creamos soluciones VR a medida que se adaptan perfectamente a las necesidades específicas.',
    icon: '🔧',
    features: [
      'Análisis de requisitos',
      'Diseño personalizado',
      'Desarrollo a medida',
      'Testing exhaustivo',
      'Soporte post-lanzamiento',
    ],
    technologies: ['Custom Development', 'VR/AR', '3D Modeling', 'Programming'],
    priceRange: { min: 20000, max: 200000, currency: 'USD' },
    duration: '16-32 semanas',
    category: 'desarrollo-personalizado',
  },
  {
    id: '6',
    title: 'Apps VR Móviles',
    description: 'Desarrollamos aplicaciones de realidad virtual optimizadas para dispositivos móviles.',
    icon: '📱',
    features: [
      'Optimización móvil',
      'Compatibilidad multiplataforma',
      'Rendimiento optimizado',
      'Integración nativa',
      'App Store deployment',
    ],
    technologies: ['React Native', 'Unity Mobile', 'ARKit', 'ARCore'],
    priceRange: { min: 8000, max: 60000, currency: 'USD' },
    duration: '8-16 semanas',
    category: 'apps-vr',
  },
]

export const createServicesSlice: StateCreator<ServicesSlice> = (set, get) => ({
  services: {
    items: mockServices,
    isLoading: false,
    error: null,
    selectedCategory: 'all',

    fetchServices: async () => {
      set((state) => ({
        services: {
          ...state.services,
          isLoading: true,
        },
      }))

      try {
        // Simular llamada a API
        await new Promise((resolve) => setTimeout(resolve, 1000))

        set((state) => ({
          services: {
            ...state.services,
            items: mockServices,
            isLoading: false,
          },
        }))
      } catch (error) {
        set((state) => ({
          services: {
            ...state.services,
            isLoading: false,
            error: 'Error al cargar los servicios',
          },
        }))
      }
    },

    setSelectedCategory: (category: ServiceCategory | 'all') => {
      set((state) => ({
        services: {
          ...state.services,
          selectedCategory: category,
        },
      }))
    },

    getServicesByCategory: (category: ServiceCategory | 'all') => {
      const { items } = get().services
      if (category === 'all') return items
      return items.filter((service) => service.category === category)
    },
  },
}) 